import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MobileStaffLayout from "../../components/mobile-staff/MobileStaffLayout";
import api from "../../api/client";
import { fmtXOF } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import {
  Building2, Users, Wallet, Sparkles, ArrowRight, TrendingUp, Calculator,
  Plus, AlertCircle, FileSignature,
} from "lucide-react";

export default function StaffDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recents, setRecents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, leads, paiements] = await Promise.all([
          api.get("/dashboard").then((r) => r.data).catch(() => null),
          api.get("/prospects", { params: { statut: "NOUVEAU" } }).then((r) => r.data).catch(() => []),
          api.get("/paiements", { params: { statut: "en_attente" } }).then((r) => r.data).catch(() => []),
        ]);
        setStats({
          ...(s || {}),
          new_leads: (leads || []).length,
          pending_paiements: (paiements || []).length,
        });
        setRecents((leads || []).slice(0, 4));
      } catch {/* silent */}
    })();
  }, []);

  const firstName = user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "";

  return (
    <MobileStaffLayout>
      <div className="px-5 pt-6 pb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-1">// TABLEAU DE BORD</div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Bonjour {firstName}
        </h2>
        <p className="text-xs sm:text-sm text-[#4B5775] mt-1">{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>

      {/* Stats grid */}
      <div className="px-5 grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard icon={Building2} label="Unités" value={stats?.unites_total ?? "—"} sub={`${stats?.unites_disponibles ?? 0} dispo`} color="bg-[#E8EBFA] text-[#1E3A8A]" />
        <StatCard icon={Users} label="Clients" value={stats?.clients_total ?? "—"} sub={`${stats?.contrats_actifs ?? 0} actifs`} color="bg-[#FCE7F3] text-[#9D174D]" />
        <StatCard icon={Wallet} label="À encaisser" value={fmtXOF(stats?.montant_attendu_mois ?? 0).replace(" FCFA", "")} sub="ce mois" color="bg-[#E5F0E8] text-[#2D7A3F]" small />
        <StatCard icon={Sparkles} label="Nouveaux leads" value={stats?.new_leads ?? "—"} sub="à contacter" color="bg-[#FFF4E5] text-[#7A5A1A]" />
      </div>

      {/* Quick actions */}
      <div className="px-5 mb-5">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#4B5775] mb-2">Actions rapides</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <QuickAction to="/encaissement" icon={Wallet} label="Encaisser" color="bg-[#1E3A8A]" />
          <QuickAction to="/prospects" icon={Sparkles} label="Nouveau prospect" color="bg-[#E91E63]" />
          <QuickAction to="/credit-sim" icon={Calculator} label="Simuler crédit" color="bg-[#2D7A3F]" />
          <QuickAction to="/clients" icon={Users} label="Clients" color="bg-[#7A5A1A]" />
        </div>
      </div>

      {/* Recent leads */}
      <div className="px-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#4B5775]">Prospects à recontacter</div>
          <Link to="/prospects" className="text-[11px] text-[#1E3A8A] font-bold inline-flex items-center gap-1">Voir tout <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="space-y-2">
          {recents.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E2E5F1] rounded-xl p-6 text-center">
              <Sparkles className="w-7 h-7 text-[#1E3A8A] mx-auto opacity-50 mb-2" />
              <div className="text-sm font-bold text-[#0F1A3D]">Aucun prospect en attente</div>
              <div className="text-xs text-[#4B5775] mt-0.5">Tout est traité — bravo !</div>
            </div>
          ) : (
            recents.map((l) => (
              <div key={l.id} className="bg-white border border-[#E2E5F1] rounded-xl p-3 flex items-center gap-3" data-testid={`ms-lead-${l.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#FCE7F3] text-[#9D174D] flex items-center justify-center text-xs font-bold shrink-0">{(l.nom || "?").slice(0, 2).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#0F1A3D] truncate">{l.nom}</div>
                  <div className="text-[11px] text-[#4B5775] truncate font-mono">{l.telephone}</div>
                  {l.bien_ref && <div className="text-[10px] text-[#9CA3B5] truncate mt-0.5">📍 {l.bien_ref}</div>}
                </div>
                <a href={`https://wa.me/${(l.telephone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-md bg-[#25D366] text-white">WhatsApp</a>
              </div>
            ))
          )}
        </div>
      </div>
    </MobileStaffLayout>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, small }) {
  return (
    <div className="bg-white border border-[#E2E5F1] rounded-xl p-3 sm:p-4">
      <div className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></div>
      <div className={`font-display font-extrabold text-[#0F1A3D] mt-2 ${small ? "text-lg" : "text-2xl"}`} style={{ fontFamily: "'Playfair Display', serif" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold">{label}</div>
      {sub && <div className="text-[10px] text-[#9CA3B5] mt-0.5">{sub}</div>}
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, color }) {
  return (
    <Link to={to} className={`${color} text-white rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 active:opacity-80 transition`} data-testid={`ms-qa-${to.replace(/\//g, "")}`}>
      <Icon className="w-5 h-5" />
      <span className="text-[10px] font-bold uppercase tracking-wider text-center leading-tight">{label}</span>
    </Link>
  );
}
